import { Link } from "react-router-dom";
import { fetchDashboardDataFromSheet } from "../../api/sheetsApi";
// pages/Kvr.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  Users,
  GraduationCap,
  Wallet,
  Bus,
  UserRound,
  CalendarDays,
  SquarePen,
  School,
  ClipboardList,
  Percent,
  FileSpreadsheet,
} from "lucide-react";

const statStyles = [
  { icon: Users, bg: "bg-pink-50", iconBg: "bg-fuchsia-700", text: "text-fuchsia-700" },
  { icon: Building2, bg: "bg-sky-50", iconBg: "bg-sky-600", text: "text-sky-600" },
  { icon: School, bg: "bg-indigo-50", iconBg: "bg-indigo-600", text: "text-indigo-600" },
  { icon: ClipboardList, bg: "bg-purple-50", iconBg: "bg-purple-600", text: "text-purple-600" },
  { icon: GraduationCap, bg: "bg-rose-50", iconBg: "bg-red-500", text: "text-red-500" },
  { icon: UserRound, bg: "bg-orange-50", iconBg: "bg-orange-500", text: "text-orange-600" },
  { icon: Users, bg: "bg-amber-50", iconBg: "bg-amber-500", text: "text-amber-600" },
  { icon: Bus, bg: "bg-cyan-50", iconBg: "bg-cyan-500", text: "text-cyan-600" },
  { icon: Bus, bg: "bg-teal-50", iconBg: "bg-teal-500", text: "text-teal-600" },
  { icon: UserRound, bg: "bg-yellow-50", iconBg: "bg-yellow-500", text: "text-yellow-600" },
  { icon: Percent, bg: "bg-red-50", iconBg: "bg-red-500", text: "text-red-500" },
  { icon: Wallet, bg: "bg-lime-50", iconBg: "bg-lime-500", text: "text-lime-600" },
  { icon: FileSpreadsheet, bg: "bg-emerald-50", iconBg: "bg-emerald-500", text: "text-emerald-600" },
];

const parseNumber = (value) => {
  const parsed = Number(String(value || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const clampPercent = (value) => `${Math.min(Math.max(value, 0), 100).toFixed(1)}%`;

const hiddenEvents = new Set(["Academic Performance"]);

export default function Kvr() {
  const [campusData, setCampusData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState("May 2026");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchKvrCampusData = async () => {
      try {
        const data = await fetchDashboardDataFromSheet();
        setCampusData(data?.KVRCampusModelData || []);
        setCurrentMonth(data?.currentMonth || "May 2026");
      } catch (err) {
        console.error(err);
        setError(err.message || "Unable to load KVR campus data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchKvrCampusData();
  }, []);

  const campusMap = useMemo(
    () =>
      campusData.reduce((acc, item) => {
        acc[item.events] = item.summary;
        return acc;
      }, {}),
    [campusData]
  );

  const stats = useMemo(
    () =>
      campusData
        .filter((item) => !hiddenEvents.has(item.events))
        .map((item, index) => ({
          title: item.events,
          value: item.summary,
          subtitle: "KVR Campus",
          ...statStyles[index % statStyles.length],
        })),
    [campusData]
  );

  const metrics = useMemo(() => {
    const students = parseNumber(campusMap["Total Students"]);
    const capacity = parseNumber(campusMap["Total Capacity"]);
    const classrooms = parseNumber(campusMap["Total Classrooms"]);
    const vacantClassrooms = parseNumber(campusMap["Total Vacant Classrooms"]);
    const capacityUtilised = capacity ? clampPercent((students / capacity) * 100) : "0.0%";
    const vacantClassroomPercent = classrooms
      ? clampPercent((vacantClassrooms / classrooms) * 100)
      : "0.0%";

    return [
      {
        title: "Capacity Utilised",
        value: capacityUtilised,
        width: capacityUtilised,
        color: "bg-fuchsia-700",
      },
      {
        title: "Fee Collected",
        value: campusMap["Term I Fees Collected %"] || "0%",
        width: campusMap["Term I Fees Collected %"] || "0%",
        color: "bg-blue-600",
      },
      {
        title: "TC Percentage",
        value: campusMap["Total TC Percentage 25-26"] || campusMap["Total TC Percentage"] || "0%",
        width: campusMap["Total TC Percentage 25-26"] || campusMap["Total TC Percentage"] || "0%",
        color: "bg-red-500",
      },
      {
        title: "Vacant Classrooms",
        value: vacantClassroomPercent,
        width: vacantClassroomPercent,
        color: "bg-yellow-400",
      },
    ];
  }, [campusMap]);

  const breakdown = useMemo(
    () => [
      {
        label: `Teachers ${campusMap["Total Teachers"] || 0}`,
        color: "bg-blue-600",
      },
      {
        label: `Admin ${campusMap["Total Admin Staff"] || 0}`,
        color: "bg-red-500",
      },
      {
        label: `Housekeeping ${campusMap["Total Housekeeping Staff"] || 0}`,
        color: "bg-yellow-400",
      },
      {
        label: `Drivers ${campusMap["Total Drivers"] || 0}`,
        color: "bg-lime-500",
      },
    ],
    [campusMap]
  );

  if (loading) {
    return (
      <div className="p-2 bg-gray-100 min-h-[100dvh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-gray-100 p-4">
        <div className="rounded-xl bg-white px-5 py-4 text-sm font-medium text-red-500 shadow-sm">
          <p>{error}</p>
          {/* <p className="mt-2 text-xs font-normal text-slate-500">
            API: /casagrand/KVRCampus/getAll
          </p> */}
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-100 min-h-[100dvh] space-y-6">
      {/* Top Banner */}
      <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
        <div className="flex flex-col lg:flex-row">
          {/* Image */}
          <div className="h-[120px] w-full lg:w-[240px]">
            <img
 src="/KVR.PNG"
               alt="campus"
              className="h-full w-full object-cover"
            />
          </div>

          {/* Content */}
          <div className="relative flex flex-1 items-center justify-between overflow-hidden bg-gradient-to-r from-violet-700 to-purple-500 px-6 py-5 text-white">
            <div className="absolute inset-0 opacity-10" />

            <div className="relative z-10">
              <h1 className="text-2xl font-semibold">KVR Campus</h1>
              <p className="mt-1 text-sm text-violet-100">
                Established Campus | Chennai
              </p>
            </div>

            <div className="relative z-10 flex flex-wrap gap-3">
              {/* Calendar Button */}
<button className="flex items-center justify-center gap-2 w-38 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition hover:bg-white/20">
  <CalendarDays size={18} />
  <span className="text-base">{currentMonth}</span>
</button>

{/* Edit Button */}
<Link data-html2canvas-ignore="true" to={"/edit/kvr"} className="flex items-center justify-center gap-2 w-38 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-purple-700 transition hover:bg-purple-50">
  <SquarePen size={18} />
  <span className="text-base">Edit</span>
</Link>

            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        {stats.map((item) => {
          const Icon = item.icon;

          return (
            <div
              key={item.title}
              className={`${item.bg} rounded-2xl p-4 shadow-sm`}
            >
              <div
                className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${item.iconBg}`}
              >
                <Icon className="text-white" size={18} />
              </div>

              <p className="text-sm text-gray-700">{item.title}</p>

              <h2 className="mt-1 break-words text-2xl font-bold text-slate-800">
                {item.value}
              </h2>

              <p className={`mt-0.5 text-xs ${item.text}`}>{item.subtitle}</p>
            </div>
          );
        })}
      </div>

      {/* Bottom Section */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Staff Breakdown */}
        <div className="rounded-3xl bg-white p-6 shadow-sm">
          <h2 className="mb-6 text-2xl font-semibold text-slate-800">
            Staff Breakdown
          </h2>

          <div className="flex flex-col items-center gap-8 lg:flex-row">
            {/* Circle */}
            <div className="relative h-44 w-44">
              <div className="absolute inset-0 rounded-full border-[14px] border-sky-500 border-r-red-500 border-b-yellow-400 border-l-lime-500" />

              <div className="absolute inset-6 flex flex-col items-center justify-center rounded-full bg-white">
                <h3 className="text-3xl font-bold text-slate-800">100%</h3>
                <p className="text-xs text-gray-500">TOTAL</p>
              </div>
            </div>

            {/* Legends */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
              {breakdown.map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div className={`h-4 w-4 rounded-full ${item.color}`} />
                  <span className="text-sm text-slate-700">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="rounded-3xl bg-white p-6 shadow-sm">
          <h2 className="mb-8 text-2xl font-semibold text-slate-800">
            Campus Metrics
          </h2>

          <div className="space-y-8">
            {metrics.map((item) => (
              <div key={item.title}>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm text-slate-700">{item.title}</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {item.value}
                  </p>
                </div>

                <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-full rounded-full ${item.color}`}
                    style={{ width: item.width }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
